import React, { useLayoutEffect } from 'react';
import { animated, easings, useTransition } from '@react-spring/web';
import { Box } from '@mentimeter/ragnar-ui/box';

interface WithKeyProp {
  key: string;
}

interface TransitionComponentProps {
  items: React.ReactElement<WithKeyProp>[]; // Is there a way to enforce children to have a key property?
  itemWidth: number;
}

/**
 * Due to the current limitation and a bug we cannot use `useTransition` with the dependency array. See https://github.com/pmndrs/react-spring/issues/2189
 * As a workaround we have to wrap the `useTransition` in a component and pass `itemWidth` as a prop and conditionally render this component only after `itemWidth` is calculated.
 */
const TransitionComponent = ({
  items,
  itemWidth,
}: TransitionComponentProps) => {
  const transitions = useTransition(items, {
    keys: (item) => item.key ?? '',
    from: { opacity: 1, width: itemWidth },
    enter: {
      opacity: 1,
      x: 0,
      width: itemWidth,
    },
    leave: {
      opacity: 0,
      x: -50,
      width: 0,
      delay: 0,
    },
    config: {
      duration: 250,
      easing: easings.easeInOutCubic, // good ones: easeOutExpo, easeOutCubic, easeInOutExpo, easeOutSine
    },
  });

  return transitions((style, item, _, i) => {
    return item ? (
      <animated.div key={i} style={{ ...style, overflow: 'hidden' }}>
        {item}
      </animated.div>
    ) : null;
  });
};

/**
 * AnimatedGrid is a component that animates out its children.
 * Note that the children must have a key.
 * Currently we have a limitation that the change of `numberOfColumns` will not be reflected i.e it will stay the same as the first value it had when rendering.
 * This is due to the fact that `useTransition` doesn't work with the dependency array. See https://github.com/pmndrs/react-spring/issues/2189
 */
export const AnimatedGrid = React.memo(
  ({
    children,
    gap = 16,
    numberOfColumns = 3,
    ...restProps
  }: {
    children: React.ReactElement<WithKeyProp>[];
    gap?: number;
    numberOfColumns?: number;
  }) => {
    const [itemWidth, setItemWidth] = React.useState(0);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const itemWidth =
          (containerWidth - gap * (numberOfColumns - 1)) / numberOfColumns;
        setItemWidth(itemWidth);
      }
    }, [gap, numberOfColumns]);

    const shouldRenderTransition = itemWidth > 0;

    return (
      <Box
        ref={containerRef}
        width="100%"
        flexWrap="wrap"
        flexDirection="row"
        gap={`${gap}px`}
        {...restProps}
      >
        {shouldRenderTransition && (
          <TransitionComponent items={children} itemWidth={itemWidth} />
        )}
      </Box>
    );
  },
);
